import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL, API_ENDPOINTS, DEFAULT_HEADERS, getAuthHeaders } from '../config/api.ts';
import { useAuth } from './AuthContext.tsx';

export interface Personnel {
  _id: string;
  name: string;
  position: string;
  email: string;
  phone: string;
  role: 'hotel_staff' | 'hotel_admin';
  branch?: {
    _id: string;
    name: string;
    code: string;
  };
  isActive: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  startDate: string;
  lastLogin?: string;
}

interface PersonnelContextType {
  personnel: Personnel[];
  branches: any[];
  loading: boolean;
  error: string | null;
  addPersonnel: (personnel: Omit<Personnel, '_id' | 'startDate' | 'lastLogin'>) => Promise<boolean>;
  deletePersonnel: (id: string) => Promise<boolean>;
  updatePersonnel: (id: string, personnel: Partial<Personnel>) => Promise<boolean>;
  getPersonnelCount: () => number;
  getActivePersonnelCount: () => number;
  fetchPersonnel: () => Promise<void>;
  fetchBranches: () => Promise<void>;
}

const PersonnelContext = createContext<PersonnelContextType | undefined>(undefined);

export const usePersonnel = () => {
  const context = useContext(PersonnelContext);
  if (context === undefined) {
    throw new Error('usePersonnel must be used within a PersonnelProvider');
  }
  return context;
};

interface PersonnelProviderProps {
  children: ReactNode;
}

export const PersonnelProvider: React.FC<PersonnelProviderProps> = ({ children }) => {
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { token, isAuthenticated, isInitialized } = useAuth();

  // Staff listesini API'den çek
  const fetchPersonnel = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated || !token) {
        throw new Error('Token bulunamadı');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_ALL}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      const data = await response.json();

      if (data.success) {
        setPersonnel(data.staff);
      } else {
        throw new Error(data.message || 'Staff listesi getirilemedi');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  };

  // Branches listesini API'den çek
  const fetchBranches = async () => {
    try {
      if (!isAuthenticated || !token) {
        throw new Error('Token bulunamadı');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_BRANCHES}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      const data = await response.json();

      if (data.success) {
        setBranches(data.branches);
      } else {
        throw new Error(data.message || 'Şubeler getirilemedi');
      }
    } catch (error) {
      // Fetch branches error
    }
  };

  // Yeni staff ekle
  const addPersonnel = async (newPersonnel: Omit<Personnel, '_id' | 'startDate' | 'lastLogin'>): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated || !token) {
        throw new Error('Token bulunamadı');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_ADD}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(newPersonnel),
      });

      const data = await response.json();

      if (data.success) {
        await fetchPersonnel(); // Listeyi yenile
        return true;
      } else {
        throw new Error(data.message || 'Staff eklenemedi');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Bilinmeyen hata');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Staff güncelle
  const updatePersonnel = async (id: string, updatedPersonnel: Partial<Personnel>): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated || !token) {
        throw new Error('Token bulunamadı');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_UPDATE}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(updatedPersonnel),
      });

      const data = await response.json();

      if (data.success) {
        await fetchPersonnel(); // Listeyi yenile
        return true;
      } else {
        throw new Error(data.message || 'Staff güncellenemedi');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Bilinmeyen hata');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Staff sil
  const deletePersonnel = async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated || !token) {
        throw new Error('Token bulunamadı');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_DELETE}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      });

      const data = await response.json();

      if (data.success) {
        await fetchPersonnel(); // Listeyi yenile
        return true;
      } else {
        throw new Error(data.message || 'Staff silinemedi');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Bilinmeyen hata');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getPersonnelCount = () => personnel.length;

  const getActivePersonnelCount = () => personnel.filter(p => p.isActive).length;

  // Component mount olduğunda ve authentication durumu değiştiğinde verileri çek
  useEffect(() => {
    if (isInitialized && isAuthenticated && token) {
      fetchPersonnel();
      fetchBranches();
    }
  }, [isInitialized, isAuthenticated, token]);

  const value: PersonnelContextType = {
    personnel,
    branches,
    loading,
    error,
    addPersonnel,
    deletePersonnel,
    updatePersonnel,
    getPersonnelCount,
    getActivePersonnelCount,
    fetchPersonnel,
    fetchBranches,
  };

  return (
    <PersonnelContext.Provider value={value}>
      {children}
    </PersonnelContext.Provider>
  );
};